/**
 * Server refusal -> localization KEY, for the locale write flows.
 *
 * WHY THIS EXISTS. `API.jsx` synthesises every HTTP failure into the legacy
 * envelope `{result: -1, remarks: <detail>}`, where `remarks` is FastAPI's
 * `detail` VERBATIM — English, written for logs and for the API contract, not
 * for a teacher. `CreateLocaleDialog.createLocaleFail` then preferred that
 * string over its own localized `CREATE_FAILED`, so a school that raced past
 * the quota pre-check was shown the literal `locale quota exhausted` — in
 * English, seconds after being shown the Chinese reminder that names the very
 * same rule.
 *
 * Fixing that by translating the ONE string would leave the next one in
 * English, so this maps the whole set the three locale routes can produce.
 *
 * RETURNS A KEY NAME, NOT A SENTENCE. That keeps the module pure — no React,
 * no `Localization` import, so its table can be unit-tested against both
 * language files at once — and matches the house pattern already used by
 * `EDConnectDialog`'s `startErrorKey` and `edconnectBind`'s `stringKey`. The
 * caller does `Localization.strings[key] || <its own generic>`.
 *
 * DEFAULT DENY. An unrecognised remark maps to `null`, and the caller shows its
 * own generic sentence. That is the point of the module: the raw server text
 * belongs in `console`, never in a toast. Adding a mapping is a deliberate act
 * that says "this string is a true, useful sentence for the person reading it".
 */

/** `_UNLIMITED`-style sentinels aside, these remarks carry no information. */
const MEANINGLESS_REMARKS = ['', 'null', 'undefined'];

/**
 * Remarks -> key. Lowercased on both sides: the same condition is spelled
 * `locale quota exhausted` by the /api route and `Locale quota limit reached`
 * by the service underneath it, and matching case-sensitively would catch one
 * and miss the other.
 *
 * Every entry below is a sentence the reader can ACT on. See the module docs
 * for why the rest are deliberately absent.
 */
const REMARK_KEYS = {
  // Out of slots. The same words the quota pre-check already uses when it
  // refuses to open the dialog at all (`LocaleViewerBar.getEpackQuotaOK`,
  // `ResourceLibraryPage.getEpackQuotaForDuplicateOK`), so a user who raced
  // past that check now gets the same explanation rather than a different one.
  'locale quota exhausted': 'LOCALE_LIMIT_MESSAGE',
  'locale quota limit reached': 'LOCALE_LIMIT_MESSAGE',

  // NOT the same thing, and the backend goes out of its way to keep them apart:
  // "you spent all your slots" is false for a school that was never entitled to
  // any. This is the `無法建立教件` case.
  'no unexpired subscription': 'LOCALE_CREATE_BLOCKED_MESSAGE',

  // Duplicate only. Two fused meanings on purpose (the route will not confirm
  // whether a locale exists), so the sentence has to name both. Deliberately
  // NOT `DUPLICATE_NOT_ALLOWED_MESSAGE`, which blames the caller's own
  // subscription — the wrong cause, and a confusing one to be told.
  'source epack not found or not duplicatable': 'DUPLICATE_SOURCE_UNAVAILABLE_MESSAGE',

  // A 401 can reach these callbacks with nothing else shown: `authFetch`
  // refreshes once and hands a still-401 response back to the caller.
  'missing bearer token': 'SESSION_EXPIRED',
  'invalid or expired token': 'SESSION_EXPIRED',
  'token revoked': 'SESSION_EXPIRED',

  // Pool saturation. Genuinely retryable, and the retry usually works.
  'database busy, please retry': 'SERVER_BUSY_RETRY',
};

/** Deleting something a colleague already deleted — only meaningful on delete. */
const NOT_FOUND_KEYS = { delete: 'LOCALE_GONE_MESSAGE' };

/**
 * The server's own words for a failure, trimmed, or `''` when it said nothing
 * usable. For `console`, never for the screen.
 *
 * `HTTP 409` and friends are treated as silence: that is what `API.jsx`
 * synthesises when the response carried no body at all. So is the literal
 * `"null"`, which is what `JSON.stringify` makes of a null `detail`.
 */
export function failureRemark(err) {
  try {
    let raw = '';
    if (typeof err === 'string') {
      raw = err;
    } else if (err && err.body && typeof err.body.remarks === 'string') {
      raw = err.body.remarks;
    }
    const remark = String(raw).trim();
    if (MEANINGLESS_REMARKS.indexOf(remark.toLowerCase()) >= 0) {
      return '';
    }
    if (/^HTTP \d+$/i.test(remark)) {
      return '';
    }
    return remark;
  } catch (e) {
    return '';
  }
}

/**
 * The localization key for this failure, or `null` to fall back to the caller's
 * own generic message.
 *
 * `context` is `'create' | 'duplicate' | 'delete'` and exists for exactly one
 * entry: `not found` means the LOCALE on a delete, but the cover FILE on a
 * create, so it can only be turned into a sentence when we know which route
 * asked.
 *
 * Wrapped in try/catch and never throws. It runs inside an API failure
 * callback: a second error thrown on top of the first would replace a bad
 * message with no message at all.
 */
export function serverErrorKey(err, context) {
  try {
    const status = (err && typeof err === 'object' && typeof err.status === 'number') ? err.status : -1;
    const remark = failureRemark(err);

    // Transport failure — no HTTP status at all, and `remarks` is whatever
    // English the browser put in the exception. Discriminated by status so we
    // never match on that text.
    if (status === 0) {
      return 'NETWORK_ERROR';
    }

    // FastAPI validation. `detail` is a LIST here, which `API.jsx` JSON-stringifies,
    // so the remark is a raw JSON blob — reachable with real input (an emoji in
    // the name: the column is utf8mb3). Whatever happens, the blob must not be
    // what the user reads.
    if (status === 422 || remark.charAt(0) === '[' || remark.charAt(0) === '{') {
      return 'SERVER_REJECTED_INPUT';
    }

    const needle = remark.toLowerCase();
    if (needle === '') {
      return null;
    }
    if (Object.prototype.hasOwnProperty.call(REMARK_KEYS, needle)) {
      return REMARK_KEYS[needle];
    }
    if (needle === 'not found') {
      return NOT_FOUND_KEYS[context] || null;
    }
    return null;
  } catch (e) {
    return null;
  }
}

/**
 * Did the server refuse this write because the school is out of locale slots?
 * Separate from the message so a caller can also react to it (the balance it
 * showed a moment ago is now known to be stale).
 */
export function isLocaleQuotaRefusal(err) {
  return serverErrorKey(err) === 'LOCALE_LIMIT_MESSAGE';
}

export default serverErrorKey;
